import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
  Alert,
  Tab,
  Tabs,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  VoiceChat as VoiceIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import ChunksList from './ChunksList';
import { sanitizeErrorMessage } from '../../utils/errorMessage';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-tabpanel-${index}`}
      aria-labelledby={`job-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const AudioJobDetailsModal = ({ open, onClose, job, isAdmin = false }) => {
  const [tabValue, setTabValue] = useState(0);

  if (!job) return null;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing':
      case 'extracting':
      case 'translating': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Audio Translation Job Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="job details tabs">
            <Tab label="Overview" />
            {isAdmin && <Tab label="Chunks Analysis" />}
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Job Overview */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>{job.video_title}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  label={job.status}
                  color={getStatusColor(job.status)}
                  size="small"
                />
                <Chip
                  label={`${job.source_language.toUpperCase()} → ${job.target_language.toUpperCase()}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={formatDuration(job.video_duration)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Job Information</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Job ID</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {job.id}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(job.id, 'Job ID')}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">YouTube URL</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {job.youtube_url}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">Progress</Typography>
                  <Typography variant="body2">{job.progress}%</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Timestamps</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Created</Typography>
                  <Typography variant="body2">
                    {format(new Date(job.created_at), 'MMM d, yyyy HH:mm:ss')}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary">Updated</Typography>
                  <Typography variant="body2">
                    {format(new Date(job.updated_at), 'MMM d, yyyy HH:mm:ss')}
                  </Typography>
                </Box>
                
                {job.completed_at && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Completed</Typography>
                    <Typography variant="body2">
                      {format(new Date(job.completed_at), 'MMM d, yyyy HH:mm:ss')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Voice Cloning Info */}
            {job.voice_clone_created && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  <VoiceIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'text-bottom' }} />
                  Voice Cloning
                </Typography>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Voice cloning was used for consistency across chunks.
                  </Typography>
                  {job.cloned_voice_id && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">Voice ID:</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {job.cloned_voice_id}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => copyToClipboard(job.cloned_voice_id, 'Voice ID')}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Alert>
              </Grid>
            )}

            {/* Download Links */}
            {job.status === 'completed' && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Download Links</Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  {job.original_audio_url && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={job.original_audio_url}
                      target="_blank"
                    >
                      Original Audio
                    </Button>
                  )}
                  {job.translated_audio_url && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      href={job.translated_audio_url}
                      target="_blank"
                    >
                      Translated Audio
                    </Button>
                  )}
                </Box>
              </Grid>
            )}

            {/* Error Message */}
            {job.error_message && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="body2">{sanitizeErrorMessage(job.error_message)}</Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {isAdmin && (
          <TabPanel value={tabValue} index={1}>
            <ChunksList jobId={job.id} isAdmin={isAdmin} />
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AudioJobDetailsModal;